import React, { useEffect, useState } from 'react';
import { dollarsToCents } from '../../utils/formatters';
import { numericRegex, dollarAmountRegex, isBlank, updateRequiredField, stages } from '../../utils/dailyPaymentFormHelpers';
import { isSuccessfulResponse } from '../../utils/responseHelpers';
import { LenderService, PaymentService } from '../../services';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import { useNotification } from '../../contexts/NotificationContext';
import { useError } from '../../contexts/ErrorContext';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    paper: {
        position: 'absolute',
        left: "30%",
        top: "15%",
        width: '40vw',
        height: '50vh',
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
        overflowY: 'scroll',
    },
    modalButton: {
        marginLeft: '15px',
        marginTop: '15px',
        float: 'right',
    },
    coreForm: {
        height: '50vh'
    }
}));

const NewPayment = (props) => {
    const classes = useStyles();
    const [loanNumber, setLoanNumber] = useState(null);
    const [loanNumberError, setLoanNumberError] = useState(false);
    const [stage, setStage] = useState('unstaged');
    const [amount, setAmount] = useState(null);
    const [amountError, setAmountError] = useState(false);
    const [lenders, setLenders] = useState([]);
    const [lenderId, setLenderId] = useState('');
    const [lenderError, setLenderError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { createErrorNotification } = useNotification();
    const { showError } = useError();
    const resetState = () => {
        setLoanNumber(null);
        setStage(null);
        setAmount(null);
        setLenderId('');
        setLenderError(false);
    }

    // The modal stays mounted behind `props.showNewPayment`, so the lender list
    // is fetched once rather than on every open.
    useEffect(() => {
        LenderService.getActive()
            .then(res => {
                if (res) setLenders(res);
            })
            .catch(error => showError(error, "Failed to load lenders"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSubmitting) return;

        // First check for missing required fields
        let hasMissingRequiredFields = false;
        hasMissingRequiredFields = updateRequiredField(loanNumber, setLoanNumber, setLoanNumberError) || hasMissingRequiredFields;
        // eslint-disable-next-line
        hasMissingRequiredFields = updateRequiredField(amount, setAmount, setAmountError) || hasMissingRequiredFields;

        // `lenderId` holds a numeric id, so the string-based `isBlank` helper
        // doesn't apply to it.
        const hasNoLender = lenderId === '' || lenderId == null;
        if (hasNoLender) setLenderError(true);

        if (loanNumberError || isBlank(loanNumber)) {
            createErrorNotification("Invalid loan number. Must be in format '12345'");
        } else if (amountError || isBlank(amount)) {
            createErrorNotification("Invalid Amount. Must be in format '123' or '123.45");
        } else if (hasNoLender) {
            createErrorNotification("Please select a lender");
        } else {
            // ? Note: Sent in dollars
            setIsSubmitting(true);
            try {
                const response = await PaymentService.create({
                    loanNumber: ('PP' + loanNumber),
                    stage: stage,
                    amountCents: dollarsToCents(amount),
                    paymentDate: new Date(props.selectedDate).toISOString().slice(0, 10),
                    paymentType: 'nacha',
                    lenderId: lenderId,
                });

                if (isSuccessfulResponse(response)) {
                    resetState();
                    props.toggleShowNewPayment();
                    // TODO: Improve me
                    setTimeout(() => window.location.reload(), 500)
                } else {
                    createErrorNotification("Failed to save new payment");
                }
            } catch (error) {
                showError(error, "Failed to save new payment");
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleCancel = () => {
        props.toggleShowNewPayment();
        resetState();
    }

    const handleLoanNumberChange = (event) => {
        if (numericRegex.test(event.target.value)) {
            setLoanNumberError(false);
            setLoanNumber(event.target.value);
        } else {
            setLoanNumberError(true);
        }
    }

    const handleStageChange = (event) => {
        setStage(event.target.value);
    }

    const handleLenderChange = (event) => {
        setLenderError(false);
        setLenderId(event.target.value);
    }

    const handleAmountChange = (event) => {
        if (dollarAmountRegex.test(event.target.value)) {
            setAmountError(false);
            setAmount(event.target.value);
        } else {
            setAmountError(true);
        }
    }

    const body = (
        <div className={classes.paper}>
            <h2 id="simple-modal-title">Create Payment</h2>
            <form onSubmit={handleSubmit}>
                <TextField label="Loan Number" inputProps={{ maxLength: 10 }} InputProps={{startAdornment: <InputAdornment position="start">PP</InputAdornment>,}} autoFocus={true} required={true} error={loanNumberError} onChange={handleLoanNumberChange} />
                <TextField label="Lender" select value={lenderId} fullWidth={true} margin="normal" required={true} error={lenderError} onChange={handleLenderChange} >
                    {lenders.map((lender) => (
                        <MenuItem key={lender.id} value={lender.id}>
                        {lender.longName}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField label="Stage" select value={stage} fullWidth={true} margin="normal" required={true} onChange={handleStageChange} >
                    {stages.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                        {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField label="Amount" inputProps={{ maxLength: 9 }} InputProps={{startAdornment: <InputAdornment position="start">$</InputAdornment>,}} fullWidth={true} margin="normal" required={true} error={amountError} onChange={handleAmountChange} />
                <Grid container spacing={5} justifyContent="flex-start">
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" className={classes.modalButton} onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? 'Creating...' : 'Create'}</Button>
                        <Button variant="contained" className={classes.modalButton} onClick={handleCancel}>Cancel</Button>
                    </Grid>
                </Grid>
            </form>
        </div>
    );

    return (
        <div>
            <Modal open={props.showNewPayment} onClose={props.toggleShowNewPayment} aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description" >
                {body}
            </Modal>
        </div>
    );
}

export default NewPayment;
